import { Body5 } from '../typography/Body5';
import {
  type ChangeEvent,
  type FC,
  type FocusEvent,
  type ForwardedRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type MutableRefObject,
  type PropsWithChildren,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { ErrorMessageForField } from '../base/ErrorMessageForField';
import { FormLabel } from '@components/input/FormLabel.tsx';
import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import { type TextareaProps } from '@headlessui/react';
import { Tooltip, type TooltipProps } from '../base/Tooltip';
import {
  type ValidationMode,
  type ValidationResult,
  type ValidatorFn,
} from '@funnellab/shared/src/validation';
import { type ValidationResultPosition } from '@src/hooks/validation/useValidation';
import { classNames } from '@utils/classNames';
import { formatErrorMessages, useValidation } from '@hooks/useValidation';
import { Body4 } from '../typography/Body4';

export type TransformerFn<OutType = string> = (input: string) => OutType;

type TextInputPossibleTypes =
  | 'text'
  | 'textarea'
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'submit'
  | 'tel'
  | 'url'
  | 'datetime-local'
  | 'date';

export type TextInputProps<OutType> = {
  readonly type?: TextInputPossibleTypes;
  readonly name?: string;
  readonly value?: string;
  readonly title?: ReactNode;
  readonly TitleComponent?: FC<PropsWithChildren>;
  readonly infoText?: string;
  readonly hint?: string;
  readonly placeholder?: string;
  readonly classNameContainer?: string;
  readonly classNameInput?: string;
  readonly inputTestId?: string;
  readonly maxLength?: number;
  readonly errorMessages?: Partial<Record<ValidationResult, string>>;
  readonly errorClassName?: string;
  readonly onBlur?: (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  readonly onFocus?: () => void;
  readonly additionalErrorMessage?: string | null | false;
  readonly required?: boolean;
  readonly onChange?: (value: OutType) => void;
  readonly validate?: ValidatorFn<OutType>;
  readonly regex?: string;
  readonly min?: number | string;
  readonly max?: number | string;
  readonly characterLimit?: number;
  readonly disabled?: boolean;
  /* @deprecated Use validationPosition instead **/
  readonly showError?: boolean;
  readonly suffix?: string;
  readonly leadingIcon?: IconType;
  readonly onEsc?: () => void;
  readonly onArrowUp?: () => void;
  readonly onArrowDown?: () => void;
  readonly onEnter?: () => void;
  readonly onCtrlEnter?: () => void;
  readonly autoFocus?: boolean;
  readonly id?: string;
  /** @deprecated Use validation.showValidationResults property */
  readonly validationMode?: ValidationMode;
  readonly validation?: {
    isValid: boolean;
    errorMessages: string[];
    showValidationResults?: boolean;
  };
  readonly validationPosition?: ValidationResultPosition;
  readonly passthrough?: {
    tooltip?: Partial<TooltipProps>;
    textarea?: Partial<HTMLAttributes<HTMLTextAreaElement> & TextareaProps>;
  };
  readonly isDirty?: boolean;
} & (OutType extends string
  ? {
      transform?: TransformerFn;
    }
  : {
      // transformer is required for non-string types
      transform: TransformerFn<OutType>;
    });

function shouldShowErrorMessage(
  isValid: boolean,
  value?: string,
  maxLength?: number,
  additionalErrorMessage?: string | false | null,
): boolean {
  return (
    !!(maxLength && value && value.length > maxLength * 0.9) ||
    !isValid ||
    !!additionalErrorMessage
  );
}

function TextInputInner<OutType>(
  {
    type = 'text',
    value,
    onChange: onChangeFn,
    placeholder,
    title,
    TitleComponent,
    infoText,
    hint,
    classNameContainer,
    classNameInput,
    name,
    inputTestId,
    validate: validateFn,
    maxLength,
    errorMessages = {},
    errorClassName,
    onBlur,
    onFocus,
    additionalErrorMessage,
    required,
    transform = (input) => input,
    regex,
    min,
    max,
    characterLimit,
    disabled = false,
    /** @deprecated use validationPosition instead */
    showError = true,
    suffix,
    leadingIcon,
    onCtrlEnter,
    onEnter,
    onEsc,
    onArrowUp,
    onArrowDown,
    autoFocus,
    id,
    validationMode,
    validation,
    validationPosition = 'none',
    passthrough,
    isDirty: isDirtyProp,
  }: TextInputProps<OutType>,
  ref: ForwardedRef<HTMLTextAreaElement | HTMLInputElement | null | undefined>,
) {
  const [typeState, setTypeState] = useState<TextInputPossibleTypes>(type);
  const { isValid, errorMessage, runValidation } = useValidation({
    value: value as OutType,
    validateFn: validateFn,
    required: required,
    messageKeys: errorMessages,
    maxLength: maxLength,
    regex: regex,
    min: min,
    max: max,
    validationMode: validationMode,
  });

  const [isDirty, setIsDirty] = useState(false);

  const [isValidLocal, setIsValidLocal] = useState(
    validation?.isValid ?? isValid,
  );
  const [showInvalidState, setShowInvalidState] = useState(false);

  useEffect(() => {
    if (isDirtyProp !== undefined) {
      setIsDirty(isDirtyProp);
    }
  }, [isDirtyProp]);

  useEffect(() => {
    setIsValidLocal(validation?.isValid ?? isValid);
  }, [isValid, validation?.isValid]);

  useEffect(() => {
    setTypeState(type);
  }, [type]);

  useEffect(() => {
    if (isValidLocal) {
      setShowInvalidState(false);
    } else if (validation) {
      setShowInvalidState(isDirty || !!validation.showValidationResults);
    } else {
      setShowInvalidState(!isValid);
    }
  }, [validation, isValid, isValidLocal, isDirty]);

  const handleHideClick = () => {
    setTypeState((old) => {
      if (old === 'password') {
        return 'text';
      } else if (old === 'text') {
        return 'password';
      } else {
        return old;
      }
    });
  };

  const defaultId = useId();
  id ??= defaultId;

  const textBoxStyle = classNames(
    showInvalidState ? 'border-orange-600' : '',
    suffix ? 'pr-8' : '',
  );

  const onChange = useCallback(
    (
      event: ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLInputElement>,
    ) => {
      setIsDirty(true);
      const parsedValue = transform(event.target.value) as OutType;
      onChangeFn?.(parsedValue);
      if (!isValid) {
        runValidation(parsedValue);
      }
    },
    [isValid, runValidation, onChangeFn, transform],
  );

  const onKeyDown = useCallback(
    (
      event:
        | KeyboardEvent<HTMLInputElement>
        | KeyboardEvent<HTMLTextAreaElement>,
    ) => {
      if (disabled) {
        return;
      }
      if (event.key === 'Escape' && onEsc) {
        event.preventDefault();
        event.stopPropagation();
        onEsc();
      } else if (event.key === 'Enter') {
        if ((event.ctrlKey || event.metaKey) && onCtrlEnter) {
          event.preventDefault();
          event.stopPropagation();
          onCtrlEnter();
        } else if (onEnter) {
          event.preventDefault();
          event.stopPropagation();
          onEnter();
        }
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        onArrowDown?.();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        onArrowUp?.();
      }
    },
    [disabled, onArrowDown, onArrowUp, onCtrlEnter, onEnter, onEsc],
  );

  const showErrorMessage = shouldShowErrorMessage(
    isValid,
    value,
    maxLength,
    additionalErrorMessage,
  );

  const tooltip = useMemo<Partial<TooltipProps>>(() => {
    if (!validation || validationPosition !== 'tooltip') {
      return {};
    }
    return {
      open: showInvalidState,
      content: (
        <Body5 className="!text-orange-600" data-testid="validation-error">
          {validation.errorMessages[0]}
        </Body5>
      ),
      placement: 'bottom',
      className: 'bg-white border-1 border-orange-600',
    };
  }, [showInvalidState, validation, validationPosition]);

  return (
    <div className={classNameContainer ?? ''}>
      {typeof title === 'string' ? (
        <label htmlFor={id}>
          <FormLabel
            label={title}
            infoText={infoText}
            required={required}
            valid={!showInvalidState}
            Component={TitleComponent}
            disabled={disabled}
          />
        </label>
      ) : (
        title
      )}
      <div className="relative">
        <Tooltip {...tooltip} {...passthrough?.tooltip}>
          {typeState === 'textarea' ? (
            <textarea
              key={name}
              id={id}
              className={classNames(
                textBoxStyle,
                'w-full text-slate-900 placeholder-gray rounded-xl border border-blue-100 px-4 py-3',
                'block h-full',
                'text-base font-normal',
                'focus-visible:outline-blue-500 focus-visible:border-blue-500',
                classNameInput,
                disabled && 'cursor-not-allowed !text-slate-500',
              )}
              value={value}
              onChange={onChange}
              placeholder={placeholder ?? ' '}
              name={name ?? ''}
              ref={ref as ForwardedRef<HTMLTextAreaElement>}
              data-testid={inputTestId}
              onFocus={onFocus}
              onBlur={(event) => {
                const parsedValue = transform(event.target.value) as OutType;
                runValidation(parsedValue);
                onBlur?.(event);
              }}
              readOnly={disabled}
              onKeyDown={onKeyDown}
              autoFocus={autoFocus}
              {...passthrough?.textarea}
            />
          ) : (
            <input
              key={name}
              id={id}
              className={classNames(
                textBoxStyle,
                classNameInput,
                'w-full text-slate-900 placeholder-gray rounded-xl border border-blue-100 px-4 py-3',
                'text-base font-normal',
                'focus-visible:outline-blue-500 focus-visible:border-blue-500',
                disabled && 'cursor-not-allowed !text-slate-500',
                leadingIcon && 'pl-10',
              )}
              type={typeState}
              value={value}
              onChange={onChange}
              placeholder={placeholder ?? ' '}
              name={name ?? ''}
              max={max}
              min={min}
              ref={ref as MutableRefObject<HTMLInputElement>}
              data-testid={inputTestId}
              onFocus={onFocus}
              inputMode={type === 'number' ? 'numeric' : 'text'}
              onBlur={(event) => {
                const parsedValue = transform(event.target.value) as OutType;
                runValidation(parsedValue);
                event.target.setCustomValidity('');
                onBlur?.(event);
              }}
              readOnly={disabled}
              onKeyDown={onKeyDown}
              autoFocus={autoFocus}
            />
          )}
        </Tooltip>
        {type === 'password' && (
          <button
            onClick={handleHideClick}
            type={'button'}
            tabIndex={-1}
            className={classNames(
              'absolute cursor-pointer text-slate-500 right-1 bottom-0 my-auto w-10 p-2 flex items-center justify-center',
              title ? 'top-1' : 'top-0',
            )}
          >
            <Icon
              type={IconType.Eye}
              className={classNames(
                'w-full h-full',
                disabled && '!text-slate-500',
              )}
            />
          </button>
        )}
        {suffix && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="text-slate-500 text-sm">{suffix}</span>
          </div>
        )}
        {leadingIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            <Icon type={leadingIcon} className="w-6 h-6 text-slate-500" />
          </div>
        )}
        {characterLimit && value && (
          <span className="absolute right-2 -bottom-5 text-sm text-slate-900 pointer-events-none">
            {value.length}/{characterLimit}
          </span>
        )}
      </div>
      {hint && !(showInvalidState || showErrorMessage) && (
        <Body4 className="mt-1">{hint}</Body4>
      )}
      {(showError || validationPosition === 'message') && (
        <ErrorMessageForField
          showErrorMessage={showInvalidState || showErrorMessage}
          errorClassName={errorClassName}
          errorMessage={
            validation
              ? validation.errorMessages.join(' ')
              : formatErrorMessages(
                  isValid,
                  maxLength,
                  value?.length ?? 0,
                  errorMessage,
                  additionalErrorMessage,
                )
          }
        />
      )}
    </div>
  );
}

export const TextInput = forwardRef(TextInputInner);
